import {Outlet, Route, Routes, useLocation} from "react-router-dom";
import {LocaleSwitcher} from "./LocaleContext";
import {useContext, useEffect, useRef, useState, createContext, useMemo} from "react";
import {useIntl} from "react-intl";
import {useAuthData, useCurrentUserData} from "./App";
import {Snackbar} from "./UIElements";
import {ReactComponent as StatsIcon} from "./svg/stats-icon.svg";
import './scss/Content.scss';
import StatsViewContainer from "./StatsView";
import {ChartNoAxesColumn, ExternalLink, LifeBuoy, Send} from "lucide-react";

export default function Content() {
    const location = useLocation()

    return <Routes location={location}>
        <Route path="/" element={<Contexts/>}>
            <Route index element={<StatsViewContainer />} />
            <Route path={'*'} element={<StatsViewContainer />} />
        </Route>
    </Routes>
}

function Contexts({}) {
    return <SnackbarWrapper>
        <Layout />
    </SnackbarWrapper>
}

const SnackbarContext = createContext({
    show: false,
    text: "",
    state: "", //error, warning, info, success
    autoHide: true
})

function SnackbarWrapper({children}) {
    const [snackbarData, setSnackbarData] = useState(useContext(SnackbarContext))
    const value = useMemo(() => (
        {snackbarData, setSnackbarData}
    ), [snackbarData])

    return <SnackbarContext.Provider value={value}>
        {children}
        <Snackbar show={snackbarData.show} text={snackbarData.text} state={snackbarData.state} autoHide={snackbarData.autoHide} />
    </SnackbarContext.Provider>
}

export function useSnackbar() {
    const context = useContext(SnackbarContext)
    if (context === undefined) {
        throw new Error('useSnackbar must be used inside SnackbarWrapper')
    }
    return context
}

function Layout() {
    return <div className={"content"}>
        <div className={"header"}>
            <h1><StatsIcon />Annie Stats</h1>

            <Profile />
        </div>
        <div className={"main"}>
            <Outlet />
        </div>
        <div className={"footer"}>
            <Feedback />
        </div>
    </div>
}

function Feedback() {
    return <FeedbackForm />
}

const SLACK_API_URL = 'https://slack.com/api/chat.postMessage';
const SLACK_API_TOKEN = 'xoxb-1437398224854-4976469613223-rMRqkJ2cN392gqSYGGDmsAKq';
const SLACK_CHANNEL_ID = 'C02GLEBEFPE';

const FeedbackForm = () => {
    const intl = useIntl()
    const {currentUserData} = useCurrentUserData()
    const user = currentUserData?.id
    const userText = currentUserData?.meta?.userid ? currentUserData.meta.userid + ' (' + user + ')' : user
    const emailText = currentUserData?.meta?.email || 'No email found in data'
    const app = "Stats app"
    const [showModal, setShowModal] = useState(false)
    const [feedback, setFeedback] = useState('')
    const [loadingText, setLoadingText] = useState('')
    const [allowContact, setAllowContact] = useState(false)
    const [error, setError] = useState("")
    const {setSnackbarData} = useSnackbar()

    const handleSubmit = async (e) => {
        e.preventDefault();

        setLoadingText(intl.formatMessage({ id: 'submittingFeedback', defaultMessage: 'Submitting feedback...' }))

        const now = new Date();
        const dateFormatter = new Intl.DateTimeFormat('fi-FI', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
        const formattedDate = dateFormatter.format(now)

        const payload = `token=${encodeURIComponent(SLACK_API_TOKEN)}&channel=${encodeURIComponent(SLACK_CHANNEL_ID)}&text=${encodeURIComponent(`User: ${userText}\nApp: ${app}\nDate: ${formattedDate}\nMay be contacted: ${allowContact ? 'Yes' : 'No'}\nEmail: ${emailText}\n\nFeedback:\n${feedback}`)}`

        try {
            const response = await fetch(SLACK_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: payload,
            });

            const responseData = await response.json();

            if (responseData.ok) {
                setShowModal(false)
                setFeedback('')
                setError('')
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({id:'thankYouForFeedback', defaultMessage:'Thank you for you feedback'}),
                    state: "success",
                    autoHide: true,
                })
            } else {
                console.log(responseData)
                throw new Error("Network response was not ok")
            }
        } catch (error) {
            console.error(error);
            setError(intl.formatMessage({ id: 'feedbackSubmitError', defaultMessage: 'An error occurred while submitting your feedback. Please try again.' }))
        } finally {
            setLoadingText('')
        }
    }

    return <div className={"feedback-container"}>
        {!showModal &&
        <button className={"open"} onClick={() => setShowModal(true)}>{intl.formatMessage({id:'giveFeedback', defaultMessage:'Give feedback'})}</button>
        }
        {showModal && (
            <div className="modal">
                <div className="header">
                    <h3>{intl.formatMessage({id:'giveFeedback', defaultMessage:'Give feedback'})}</h3>
                    <button className={"close"} onClick={() => setShowModal(false)}>{intl.formatMessage({id:'close', defaultMessage:'Close'})}</button>
                </div>
                <div className="modal-content">
                    <form onSubmit={handleSubmit}>
                        <textarea autoFocus value={feedback} rows={4} onChange={(e) => setFeedback(e.target.value)} />
                        <label className={"checkbox-label"}>
                            <input
                                type="checkbox"
                                id="allowContact"
                                checked={allowContact}
                                onChange={(event) => setAllowContact(event.target.checked)}
                            />
                            <label htmlFor="allowContact">
                                {intl.formatMessage({id:'contactAllowed', defaultMessage:'I may be contacted regarding my feedback'})}

                            </label>
                        </label>
                        {error && <p className="error">{error}</p>}
                        <button type="submit">
                            {loadingText ?
                                intl.formatMessage({ id: 'submittingFeedback', defaultMessage: 'Submitting feedback...' }) :
                                intl.formatMessage({id:'submit', defaultMessage:'Submit'})
                            }
                        </button>
                    </form>
                </div>
            </div>
        )}
    </div>
};


function Profile() {
    const {currentUserData} = useCurrentUserData()
    const {authData} = useAuthData()
    const firstName = currentUserData && currentUserData?.meta && currentUserData?.meta.hasOwnProperty("firstname") ? currentUserData.meta.firstname : null
    const lastName = currentUserData && currentUserData?.meta && currentUserData?.meta.hasOwnProperty("lastname") ? currentUserData.meta.lastname : null
    const userid = currentUserData?.meta?.userid ? currentUserData.meta.userid : (currentUserData?.meta?.email ? currentUserData.meta.email : authData.uid)
    const initials = (firstName && lastName) ? firstName.charAt(0) + lastName.charAt(0) : userid.substring(0, 2)
    const [showProfilePopover, setShowProfilePopover] = useState(false)
    const popoverRef = useRef(null)
    const popoverTriggerRef = useRef(null)
    const intl = useIntl()
    const logoutURL = authData.hasOwnProperty("logoutURL") ? authData.logoutURL : null

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        function handleClickOutside(event) {
            if (popoverRef.current && !popoverRef.current.contains(event.target) && !popoverTriggerRef.current.contains(event.target) ) {
                setShowProfilePopover(false)
            }
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            }
        }
    },[popoverRef])

    const externalNavItems = [
        {
            path: "/app",
            icon: <LifeBuoy size={16} strokeWidth={1.5} />,
            messageId: "app.app",
            defaultMessage: "Support Requests"
        },
        {
            path: "/admin",
            icon: <Send size={16} strokeWidth={1.5} />,
            messageId: "app.admin",
            defaultMessage: "Admin Console",
            superuser: true
        }
    ].filter(nav => !nav.superuser || currentUserData?.superuser)

    const baseUrl = `${window.location.protocol}//${window.location.hostname}`

    return <div className={"profile-container"}>
        <button className={"profile-icon"} ref={popoverTriggerRef} onClick={()=>setShowProfilePopover(!showProfilePopover)}>{initials}</button>
        {showProfilePopover &&
        <div className={"profile-popover"} ref={popoverRef}>
            <>
                <h2>
                    {(firstName && lastName) ?
                        firstName +" "+ lastName :
                        userid
                    }
                </h2>
                <hr/>
            </>

            <h4>
                {intl.formatMessage({
                    id: 'profile',
                    defaultMessage: 'Profile',
                })}
            </h4>
            <p>{userid}</p>
            <hr/>
            <h4>
                {intl.formatMessage({
                    id: 'language',
                    defaultMessage: 'Language',
                })}
            </h4>
            <LocaleSwitcher />
            <hr/>
            {externalNavItems.map((item, index) => (
                <a
                    href={`${baseUrl}${item.path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={"external-link"}
                    key={index}
                >
                    {item.icon}
                    <span>{intl.formatMessage({ id: item.messageId, defaultMessage: item.defaultMessage })}</span>
                    <ExternalLink size={12} strokeWidth={1.5} style={{ marginLeft: 'auto' }} />
                </a>
            ))}
            {logoutURL &&
            <>
                <hr/>
                <a href={logoutURL}>
                    {intl.formatMessage({
                        id: 'logOut',
                        defaultMessage: 'Log out',
                    })}
                </a>
            </>
            }

        </div>
        }
    </div>
}